import React, { useEffect, useReducer } from "react";

import { BaseModal } from "../../components/modalCommon/modalBase";
import { ModalBody } from "../../components/modalCommon/modalBody";
import { ModalFooter } from "../../components/modalCommon/modalFooter";
import { ModalHeader } from "../../components/modalCommon/modalHeader";
import LoadingSpinner from "../../components/loadingSpinner/loadingSpinner";
import { fetchHearingResultAnalysis, saveAiAnalysis } from "../../utils/hearingResultAnalysis";
import { useI18n } from "../../utils/translate/i18n/useI18n";

import "./analysisModal.scss";
import type { AnalysisModalBaseProps, AnalysisModalModel, AnalysisState } from "../../type/analitycs";

export function AnalysisModalBase<T,>({
  isOpen,
  onClose,
  parsedResult,
  demographics,
  id,
  cachedAnalysis,
  onAnalysisSaved,
  fetchFn,
  saveFn,
}: AnalysisModalBaseProps<T>) {
  const { t } = useI18n();
  const [state, dispatch] = useReducer(
    (_prev: AnalysisState, next: AnalysisState) => next,
    { status: "idle" }
  );

  useEffect(() => {
    if (!isOpen || !parsedResult) {
      return;
    }

    if (cachedAnalysis) {
      dispatch({ status: "success", text: cachedAnalysis });
      return;
    }

    dispatch({ status: "loading" });

    fetchFn(parsedResult, demographics)
      .then((content) => {
        dispatch({ status: "success", text: content });
        if (id != null && saveFn) {
          saveFn(id, content)
            .then(() => onAnalysisSaved?.(id, content))
            .catch(console.error);
        }
      })
      .catch((error) => {
        const message = error instanceof Error ? error.message : "Nie udało się pobrać analizy wyników.";
        dispatch({ status: "error", message });
      });
  }, [isOpen, parsedResult]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    if (state.status === "loading") {
      return;
    }
    onClose();
  };

  return (
    <BaseModal open={isOpen} onClose={handleClose} size="lg">
      <ModalHeader title={t("Analiza wyników")} onClose={handleClose} />
      <ModalBody>
        {state.status === "loading" && (
          <LoadingSpinner label="Trwa analiza wyników…" className="analysis-modal__state" />
        )}
        {state.status === "error" && (
          <div className="analysis-modal__error" role="alert">
            <i className="bi bi-exclamation-triangle-fill me-2" />
            {state.message}
          </div>
        )}
        {state.status === "success" && (
          <>
            <div className="analysis-modal__disclaimer">{t("Niniejsza analiza ma charakter informacyjny i nie stanowi diagnozy medycznej. W celu pełnej oceny stanu słuchu zaleca się konsultację ze naszym specjalistą ze Świata Słuchu Dąbrowska & Kowalczyk.")}</div>
            <div className="analysis-modal__content">{state.text}</div>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={handleClose}
          disabled={state.status === "loading"}
        >
          {t("Zamknij")}
        </button>
      </ModalFooter>
    </BaseModal>
  );
}

const AnalysisModal: React.FC<AnalysisModalModel> = ({
  isOpen,
  onClose,
  parsedResult,
  demographics,
  id,
  cachedAnalysis,
  onAnalysisSaved,
}) => (
  <AnalysisModalBase
    isOpen={isOpen}
    onClose={onClose}
    parsedResult={parsedResult}
    demographics={demographics}
    id={id}
    cachedAnalysis={cachedAnalysis}
    onAnalysisSaved={onAnalysisSaved}
    fetchFn={fetchHearingResultAnalysis}
    saveFn={saveAiAnalysis}
  />
);

export default AnalysisModal;
